/**
 * Effective attribute offsets (spec 2026-09-20).
 *
 * An `effective.*` modification treats an attribute as N die types higher or lower FOR ONE PURPOSE
 * — skill costs, the encumbrance limit or a weapon's minimum Strength — without touching the
 * attribute itself. The store folds every such row into a signed step offset per attribute and
 * purpose (recalculateEffectiveAttributes) and stores it on protoData.attributes.<attr>; readers
 * add the offset to the live rank. Pure — no store import, like derived-formula.ts — so the folding
 * and the storage rules have exactly one implementation.
 */
import type { SwadeModConfigurationInterface } from '@/types/items';
import type { AttributeNameType, SwadeCharacterPrototypeInterface, SwadeProtoAttributeInterface } from '@/types/characters';

export type AttributeName = Exclude<AttributeNameType, null>;
export const ATTRIBUTE_NAMES: readonly AttributeName[] = ['agility', 'smarts', 'spirit', 'strength', 'vigor'];

/** Path → the protoData field it is folded into. */
export const EFFECTIVE_PATHS = {
    'effective.skillCost':   'effective',
    'effective.encumbrance': 'encumbrance',
    'effective.minStr':      'minStr',
} as const;
export type EffectivePath  = keyof typeof EFFECTIVE_PATHS;
export type EffectiveField = (typeof EFFECTIVE_PATHS)[EffectivePath];
export const EFFECTIVE_FIELDS: readonly EffectiveField[] = ['effective', 'encumbrance', 'minStr'];

/** `{ strength: { encumbrance: 1, minStr: 1 }, smarts: { effective: -1 } }` */
export type EffectiveOffsets = Partial<Record<AttributeName, Partial<Record<EffectiveField, number>>>>;

export function isEffectivePath(path: unknown): path is EffectivePath {
    return typeof path === 'string' && Object.prototype.hasOwnProperty.call(EFFECTIVE_PATHS, path);
}

export function isAttributeName(element: unknown): element is AttributeName {
    return typeof element === 'string' && (ATTRIBUTE_NAMES as readonly string[]).includes(element);
}

/**
 * Folds walk rows into offsets: value × (subtract ? -1 : 1) × multiplier, per attribute and field.
 * A row is IGNORED, never coerced, when its value is not a POSITIVE integer (the operator alone
 * carries the sign, spec §3), its element is not one of the five attribute keys, its operator is
 * neither add nor subtract, or it is a Strength-path row whose element is not strength.
 */
export function sumEffectiveOffsets(
    rows: Iterable<{ configuration: SwadeModConfigurationInterface, multiplier: number }>,
): EffectiveOffsets {
    const offsets: EffectiveOffsets = {};
    for (const { configuration, multiplier } of rows) {
        if (!isEffectivePath(configuration.path)) continue;
        const attribute = configuration.element;
        if (!isAttributeName(attribute)) continue;
        const field = EFFECTIVE_PATHS[configuration.path];
        // The Strength paths carry strength and nothing else (spec §3): a row naming another attribute
        // is ignored, not folded onto that attribute's field, which no reader consults.
        if (field !== 'effective' && attribute !== 'strength') continue;
        const value = configuration.value;
        if (!Number.isInteger(value) || (value as number) < 1) continue;
        if (configuration.operator !== 'add' && configuration.operator !== 'subtract') continue;
        const sign = configuration.operator === 'subtract' ? -1 : 1;
        const perAttribute = (offsets[attribute] ??= {});
        perAttribute[field] = (perAttribute[field] ?? 0) + (value as number) * sign * multiplier;
    }
    return offsets;
}

/**
 * Writes every attribute × field: the integer when non-zero, deleting the key otherwise, so a sheet
 * with no effective.* rows keeps byte-identical protoData. An attribute the prototype does not
 * carry is skipped — nothing is invented for it.
 */
export function writeEffectiveOffsets(
    attributes: SwadeCharacterPrototypeInterface['attributes'],
    offsets: EffectiveOffsets,
): void {
    for (const attribute of ATTRIBUTE_NAMES) {
        const entry: SwadeProtoAttributeInterface | undefined = attributes?.[attribute];
        if (!entry) continue;
        for (const field of EFFECTIVE_FIELDS) {
            const total = offsets[attribute]?.[field] ?? 0;
            if (total === 0) delete entry[field];
            else entry[field] = total;
        }
    }
}

/** The stored offset for one attribute and field, or 0 when absent or not an integer. */
export function effectiveOffset(
    protoData: SwadeCharacterPrototypeInterface | null | undefined,
    attribute: string,
    field: EffectiveField,
): number {
    if (!isAttributeName(attribute)) return 0;
    const stored: unknown = protoData?.attributes?.[attribute]?.[field];
    return Number.isInteger(stored) ? (stored as number) : 0;
}
